<template>
  <div v-rect-loading="loading" class="score-scale-setting-page">
    <div class="scale-row">
      <div class="header">
        <div class="choose-label">{{ $t(`${i18nPrefix}.label.chooseScale`) }}</div>
        <div v-if="!isRootCompany" class="inherite-company">
          <span class="label">{{ $t(`${i18nPrefix}.label.inheritCompany`) }}</span>
          <el-popconfirm
            :confirm-button-text="$t('common.btn.confirm')"
            :cancel-button-text="$t('common.btn.cancel')"
            :title="$t(`${i18nPrefix}.message.${inheritCompanyConfirmMessageKey}`)"
            @confirm="handleInheritedChange"
            @cancel="handleInheritedCancel"
          >
            <template #reference>
              <el-switch
                :value="isInherited"
                :disabled="loading || isEditing"
                @change="handleInheritedSwitchChange"
              ></el-switch>
            </template>
          </el-popconfirm>
        </div>
      </div>
      <div class="scale-tabs">
        <el-button
          v-for="item in scoreOptions"
          :key="item.value"
          class="scale-tab"
          :class="{ active: item.value === activeScore }"
          round
          @click="handleScoreSelect(item.value)"
        >
          {{ item.label }}
        </el-button>
      </div>
      <div v-if="canEditDefinitions" class="action-buttons">
        <el-button v-if="isEditing" class="plain-btn" @click="handleCancelEdit">
          {{ $t('common.btn.cancel') }}
        </el-button>
        <el-button v-if="isEditing" class="primary-btn" @click="handleSave">
          {{ $t('common.btn.save') }}
        </el-button>
        <el-button v-else class="plain-btn" @click="handleEdit">
          {{ $t('common.btn.edit') }}
        </el-button>
      </div>
    </div>

    <TableList
      ref="table"
      class="definition-table"
      noHead
      noAction
      noPagination
      :listData="definitionRows"
      :tableColumns="tableColumns"
    >
      <template #scale="{ data }">
        <span class="scale-link">{{ $t(`${i18nPrefix}.label.point`, { score: data }) }}</span>
      </template>
      <template #description="{ row, data, dataKey }">
        <el-input
          v-if="isEditing && canEditDefinitions"
          v-model="row[dataKey]"
          type="textarea"
          class="description-input"
          :autosize="{ minRows: 1 }"
        ></el-input>
        <span v-else class="description-text">{{ data }}</span>
      </template>
    </TableList>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import { HM_SCALE_MIN_SCORE } from '@/constant/questionnaire'

export default {
  components: {
    TableList
  },
  data () {
    return {
      loading: false,
      i18nPrefix: 'admin.company.scoreScaleSetting',
      activeScore: 10,
      isEditing: false,
      isInherited: false,
      pendingInheritedValue: null,
      scales: {},
      definitionRows: []
    }
  },
  computed: {
    ...mapGetters('account', ['getCurrentAccount']),
    isRootCompany () {
      const currentAccount = this.getCurrentAccount || {}
      return Number(currentAccount.company_id) === Number(currentAccount.root_company_id)
    },
    inheritCompanyConfirmMessageKey () {
      const nextValue = _.isBoolean(this.pendingInheritedValue) ? this.pendingInheritedValue : !this.isInherited
      return nextValue ? 'confirmUseParentCompanyConfig' : 'confirmCancelParentCompanyConfig'
    },
    scoreOptions () {
      return _.chain([])
        .thru(() => _.range(1, 11))
        .map(score => ({
          value: score,
          label: this.$t(`${this.i18nPrefix}.label.scorePoint`, { score })
        }))
        .value()
    },
    activeScaleType () {
      return this.getScaleType(this.activeScore)
    },
    activeScale () {
      return _.get(this.scales, this.activeScaleType) || this.buildDefaultScale(this.activeScore)
    },
    canEditDefinitions () {
      return !this.isInherited
    },
    tableColumns () {
      return [
        {
          label: this.$t(`${this.i18nPrefix}.label.scale`),
          dataKey: 'score',
          slotName: 'scale',
          width: '72px',
          align: 'center',
          showOverflowTooltip: false
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.scaleChineseDescription`),
          dataKey: 'cnDescription',
          slotName: 'description',
          width: '328px',
          showOverflowTooltip: false
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.scaleEnglishDescription`),
          dataKey: 'enDescription',
          slotName: 'description',
          width: '328px',
          showOverflowTooltip: false
        }
      ]
    }
  },
  created () {
    this.initData()
  },
  methods: {
    ...mapActions('questionnaire', [
      'getHmScoreScaleConfig',
      'updateHmScoreScaleConfig'
    ]),
    async initData () {
      this.loading = true
      try {
        const config = await this.getHmScoreScaleConfig()
        this.applyScaleConfig(config)
      } finally {
        this.loading = false
      }
    },
    getScaleType (score) {
      return `scale_${Number(score)}`
    },
    getScoreFromScaleType (scaleType) {
      const score = Number(`${scaleType || ''}`.replace('scale_', ''))
      return score || HM_SCALE_MIN_SCORE
    },
    createDefaultDefinitions (score) {
      return _.chain([])
        .thru(() => _.range(HM_SCALE_MIN_SCORE, score + 1))
        .map(item => ({
          score: item,
          cnLabel: '',
          enLabel: '',
          cnDescription: '',
          enDescription: ''
        }))
        .value()
    },
    buildDefaultScale (score) {
      const scaleType = this.getScaleType(score)
      return {
        scaleType,
        nameZh: undefined,
        nameEn: undefined,
        minScore: HM_SCALE_MIN_SCORE,
        maxScore: score,
        score,
        definitions: this.createDefaultDefinitions(score)
      }
    },
    getTextValue (source, key, fallback) {
      const value = _.get(source, key)
      return value === undefined || value === null ? fallback : value
    },
    normalizeDefinition (value, fallback, index) {
      return {
        score: Number(_.get(value, 'score_value', fallback.score)),
        cnLabel: this.getTextValue(value, 'label_zh', fallback.cnLabel),
        enLabel: this.getTextValue(value, 'label_en', fallback.enLabel),
        cnDescription: this.getTextValue(value, 'description_zh', fallback.cnDescription),
        enDescription: this.getTextValue(value, 'description_en', fallback.enDescription),
        sortOrder: Number(_.get(value, 'sort_order')) || index + 1
      }
    },
    normalizeScale (scale, scaleType) {
      const score = Number(_.get(scale, 'max_score')) || this.getScoreFromScaleType(_.get(scale, 'scale_type') || scaleType)
      const fallbackScale = this.buildDefaultScale(score)
      const sourceValues = _.get(scale, 'values')
      const definitions = _.isArray(sourceValues) && sourceValues.length
        ? _.chain(sourceValues)
          .map((value, index) => {
            const scoreValue = Number(_.get(value, 'score_value'))
            const fallback = _.find(fallbackScale.definitions, { score: scoreValue }) || _.get(fallbackScale.definitions, index) || {}
            return this.normalizeDefinition(value, fallback, index)
          })
          .sortBy('score')
          .value()
        : fallbackScale.definitions

      return {
        ...fallbackScale,
        scaleType: _.get(scale, 'scale_type') || scaleType || fallbackScale.scaleType,
        nameZh: _.get(scale, 'name_zh'),
        nameEn: _.get(scale, 'name_en'),
        minScore: Number(_.get(scale, 'min_score')) || HM_SCALE_MIN_SCORE,
        maxScore: score,
        score,
        definitions
      }
    },
    normalizeScales (scales) {
      return _.chain([])
        .thru(() => _.range(1, 11))
        .reduce((result, score) => {
          const scaleType = this.getScaleType(score)
          const source = _.get(scales, scaleType)
          result[scaleType] = source ? this.normalizeScale(source, scaleType) : this.buildDefaultScale(score)
          return result
        }, {})
        .value()
    },
    applyScaleConfig (config) {
      this.isInherited = Boolean(_.get(config, 'inherit_parent_config'))
      this.scales = this.normalizeScales(_.get(config, 'scales', {}))
      this.resetDefinitionRows()
    },
    buildScalePayload (scale) {
      const score = Number(scale.score)
      return {
        scale_type: this.getScaleType(score),
        name_zh: scale.nameZh,
        name_en: scale.nameEn,
        min_score: HM_SCALE_MIN_SCORE,
        max_score: score,
        values: _.map(scale.definitions || [], (item, index) => ({
          score_value: Number(item.score),
          label_zh: item.cnLabel,
          label_en: item.enLabel,
          description_zh: item.cnDescription,
          description_en: item.enDescription,
          sort_order: index + 1
        }))
      }
    },
    buildScalesPayload () {
      return _.chain([])
        .thru(() => _.range(1, 11))
        .reduce((result, score) => {
          const scaleType = this.getScaleType(score)
          result[scaleType] = this.buildScalePayload(_.get(this.scales, scaleType) || this.buildDefaultScale(score))
          return result
        }, {})
        .value()
    },
    updateActiveScaleFromRows () {
      this.scales = {
        ...this.scales,
        [this.activeScaleType]: {
          ...this.activeScale,
          definitions: _.cloneDeep(this.definitionRows)
        }
      }
    },
    async saveConfig () {
      const resp = await this.updateHmScoreScaleConfig({
        inherit_parent_config: this.isInherited,
        scales: this.buildScalesPayload()
      })
      this.applyScaleConfig(resp)
    },
    normalizeDefinitions (scale) {
      const sourceDefinitions = _.get(scale, 'definitions', [])
      return _.chain([])
        .thru(() => _.range(HM_SCALE_MIN_SCORE, this.activeScore + 1))
        .map((score, index) => {
          const source = _.find(sourceDefinitions, { score }) || {}
          return {
            score,
            cnLabel: this.getTextValue(source, 'cnLabel', ''),
            enLabel: this.getTextValue(source, 'enLabel', ''),
            cnDescription: this.getTextValue(source, 'cnDescription', ''),
            enDescription: this.getTextValue(source, 'enDescription', ''),
            sortOrder: Number(_.get(source, 'sortOrder')) || index + 1
          }
        })
        .value()
    },
    resetDefinitionRows () {
      this.definitionRows = this.normalizeDefinitions(this.activeScale)
    },
    handleScoreSelect (score) {
      if (this.activeScore === score) {
        return
      }
      this.activeScore = score
      this.isEditing = false
      this.resetDefinitionRows()
    },
    handleEdit () {
      if (this.canEditDefinitions) {
        this.isEditing = true
      }
    },
    handleCancelEdit () {
      this.isEditing = false
      this.resetDefinitionRows()
    },
    handleInheritedSwitchChange (value) {
      this.pendingInheritedValue = value
    },
    handleInheritedCancel () {
      this.pendingInheritedValue = null
    },
    async handleInheritedChange () {
      const nextValue = _.isBoolean(this.pendingInheritedValue) ? this.pendingInheritedValue : !this.isInherited
      const previousValue = this.isInherited
      this.isInherited = nextValue
      this.pendingInheritedValue = null
      this.isEditing = false
      this.loading = true
      try {
        await this.saveConfig()
        this.$message.success(this.$t('common.message.saveSuccess'))
      } catch (e) {
        this.isInherited = previousValue
      } finally {
        this.loading = false
      }
    },
    async handleSave () {
      this.loading = true
      try {
        this.updateActiveScaleFromRows()
        await this.saveConfig()
        this.isEditing = false
        this.$message.success(this.$t('common.message.saveSuccess'))
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.score-scale-setting-page {
  .page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .page-title {
      color: #303133;
      font-size: 20px;
      font-weight: 600;
      line-height: 28px;
      white-space: normal;
    }

    .scale-meta {
      display: flex;
      align-items: center;
      color: #909399;
      font-size: 13px;
      line-height: 20px;

      .status-tag {
        min-width: 52px;
        min-height: 24px;
        margin-right: 10px;
        padding: 0 10px;
        border-radius: 12px;
        text-align: center;
        line-height: 18px;
        white-space: normal;

        &.status-enabled {
          background: rgba(0, 150, 204, 0.1);
          color: #0096cc;
        }

        &.status-disabled {
          background: #f5f5f5;
          color: #909399;
        }
      }
    }
  }

  .divider {
    height: 1px;
    margin: 12px 0 14px;
    background: #dcdfe6;
  }

  .scale-row {
    margin-bottom: 16px;

    .header {
      display: flex;
      justify-content: space-between;

      .label {
        margin-right: 8px;
        color: #606266;
      }
    }

    .choose-label {
      margin-bottom: 12px;
      color: #303133;
      font-size: 14px;
      line-height: 22px;
      white-space: normal;
    }

    .scale-tabs {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;

      .scale-tab {
        min-width: 78px;
        padding: 8px 16px;
        margin: 0;
        border-color: #dcdfe6;
        background: #fff;
        color: #606266;
        font-size: 12px;
        white-space: normal;

        &.active {
          border-color: #0096cc;
          background: #0096cc;
          color: #fff;
        }
      }
    }

    .action-buttons {
      display: flex;
      justify-content: flex-end;
      margin-top: 16px;

      .plain-btn,
      .primary-btn {
        min-width: 57px;
        min-height: 32px;
        padding: 0 16px;
        border-radius: 4px;
        line-height: 20px;
        white-space: normal;
      }

      .plain-btn {
        border-color: #0096cc;
        color: #0096cc;
      }

      .primary-btn {
        margin-left: 8px;
        border-color: #0096cc;
        background: #0096cc;
        color: #fff;
      }
    }
  }

  .definition-table {
    margin-top: 12px;

    .scale-link {
      color: #0096cc;
      white-space: normal;
    }

    .description-input {
      display: flex;
      align-items: center;
      min-height: 40px;
    }

    .description-text {
      display: flex;
      min-height: 40px;
      align-items: center;
      line-height: 20px;
      white-space: normal;
      word-break: normal;
    }

    ::v-deep {
      .el-table {
        .el-table__header {
          th {
            .cell {
              overflow: visible;
              white-space: normal;
              word-break: normal;
            }
          }
        }

        .el-table__body {
          td {
            .cell {
              line-height: 20px;
              overflow: visible;
              white-space: normal;
              word-break: normal;
            }
          }
        }
      }

      .el-input {
        .el-input__inner {
          height: 46px;
          border-color: #dcdfe6;
          border-radius: 4px;
          color: #606266;
          line-height: 46px;
        }
      }

      .el-textarea {
        .el-textarea__inner {
          min-height: 46px;
          padding-top: 6px;
          padding-bottom: 6px;
          border-color: #dcdfe6;
          border-radius: 4px;
          color: #606266;
          line-height: 16px;
          resize: none;
        }
      }
    }
  }
}
</style>
